import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '@/lib/firebase';
import { doc, getDoc, collection, addDoc, query, orderBy, onSnapshot, serverTimestamp, deleteDoc, where, getDocs } from 'firebase/firestore';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/ui/avatar';
import { Navbar } from '@/components/dashboard/Navbar';
import { ProfileCard } from '@/components/dashboard/ProfileCard';
import { PostCard } from '@/components/dashboard/PostCard';
import { CommentsDialog } from '@/components/dashboard/CommentsDialog';
import { ShareDialog } from '@/components/dashboard/ShareDialog';
import { CreatePostDialog } from '@/components/dashboard/CreatePostDialog';
import { RatingsCard } from '@/components/dashboard/RatingsCard';
import { LoadingSkeleton } from '@/components/dashboard/LoadingSkeleton';
import { UserProfile } from '@/types/user';
import { Post } from '@/types/post';
import { Comment } from '@/types/comment';
import { toast } from 'sonner';

interface Rating {
  id: string;
  rating: number;
  comment: string;
  authorId: string;
  authorName: string;
  createdAt: any;
}

export default function DashboardPage() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [showComments, setShowComments] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [posts, setPosts] = useState<Post[]>([]);
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [averageRating, setAverageRating] = useState(0);
  const [currentCity, setCurrentCity] = useState<string>('');
  const navigate = useNavigate();

  const loadProfile = async (city?: string) => {
    setLoading(true);
    const user = auth.currentUser;
    if (!user) {
      navigate('/');
      return;
    }

    try {
      const userProfilesRef = collection(db, 'userProfiles');
      const q = query(
        userProfilesRef,
        where('userId', '==', user.uid),
        where('city', '==', city || localStorage.getItem('currentCity'))
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const profileData = querySnapshot.docs[0].data() as UserProfile;
        setProfile(profileData);
        setCurrentCity(profileData.city);
        
        // Load posts for the selected city
        loadPosts(profileData.city);
      } else {
        // If no profile found for the selected city, get the first available profile
        const allProfilesQuery = query(userProfilesRef, where('userId', '==', user.uid));
        const allProfilesSnapshot = await getDocs(allProfilesQuery);
        
        if (!allProfilesSnapshot.empty) {
          const firstProfile = allProfilesSnapshot.docs[0].data() as UserProfile;
          setProfile(firstProfile);
          setCurrentCity(firstProfile.city);
          localStorage.setItem('currentCity', firstProfile.city);
          
          // Load posts for the first available city
          loadPosts(firstProfile.city);
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Erro ao carregar perfil');
    } finally {
      setLoading(false);
    }
  };

  const loadPosts = (city: string) => {
    try {
      const postsRef = collection(db, 'posts');
      const q = query(
        postsRef,
        where('city', '==', city),
        orderBy('createdAt', 'desc')
      );

      return onSnapshot(q, (snapshot) => {
        const postsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Post[];
        setPosts(postsData);
      }, (error) => {
        console.error('Error loading posts:', error);
        toast.error('Erro ao carregar publicações');
      });
    } catch (error) {
      console.error('Error setting up posts listener:', error);
    }
  };

  useEffect(() => {
    loadProfile();
  }, [navigate]);

  // Load ratings
  useEffect(() => {
    if (!auth.currentUser) return;

    const ratingsRef = collection(db, `users/${auth.currentUser.uid}/ratings`);
    const q = query(ratingsRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ratingsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Rating[];
      
      setRatings(ratingsData);
      
      if (ratingsData.length > 0) {
        const sum = ratingsData.reduce((acc, curr) => acc + curr.rating, 0);
        setAverageRating(sum / ratingsData.length);
      } else {
        setAverageRating(0);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleCityChange = async (city: string) => {
    setLoading(true);
    await loadProfile(city);
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      localStorage.removeItem('currentCity');
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Erro ao fazer logout');
    }
  };

  const navigateToProfile = (authorId: string) => {
    navigate(`/prestador/${authorId}`);
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedPost || !auth.currentUser) return;

    try {
      const commentRef = collection(db, 'posts', selectedPost.id.toString(), 'comments');
      await addDoc(commentRef, {
        authorId: auth.currentUser.uid,
        authorName: profile?.name || 'Usuário Anônimo',
        content: newComment,
        createdAt: serverTimestamp()
      });

      setNewComment('');
      toast.success('Comentário adicionado');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Erro ao adicionar comentário');
    }
  };

  const handleCreatePost = async (postData: { content: string; images: string[] }) => {
    if (!profile || !auth.currentUser) return;

    try {
      const postsRef = collection(db, 'posts');
      await addDoc(postsRef, {
        authorId: auth.currentUser.uid,
        authorName: profile.name,
        service: profile.services,
        content: postData.content,
        likes: 0,
        comments: 0,
        images: postData.images,
        city: currentCity,
        createdAt: serverTimestamp()
      });

      setShowCreatePost(false);
      toast.success('Publicação criada com sucesso');
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error('Erro ao criar publicação');
    }
  };

  const handleDeletePost = async (postId: string) => {
    try {
      await deleteDoc(doc(db, 'posts', postId));
      toast.success('Publicação excluída com sucesso');
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Erro ao excluir publicação');
    }
  };

  const handleReportPost = async (postId: string) => {
    try {
      const reportsRef = collection(db, 'reports');
      await addDoc(reportsRef, {
        postId,
        reportedBy: auth.currentUser?.uid,
        createdAt: serverTimestamp()
      });
      toast.success('Publicação denunciada com sucesso');
    } catch (error) {
      console.error('Error reporting post:', error);
      toast.error('Erro ao denunciar publicação');
    }
  };

  const handleSavePost = async (postId: string) => {
    try {
      const savedPostsRef = collection(db, 'savedPosts');
      await addDoc(savedPostsRef, {
        postId,
        userId: auth.currentUser?.uid,
        createdAt: serverTimestamp()
      });
      toast.success('Publicação salva com sucesso');
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error('Erro ao salvar publicação');
    }
  };

  const loadComments = (postId: string) => {
    const commentsRef = collection(db, 'posts', postId, 'comments');
    const q = query(commentsRef, orderBy('createdAt', 'desc'));

    return onSnapshot(q, (snapshot) => {
      const commentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Comment[];
      setComments(commentsData);
    });
  };

  const handleShare = async (type: string, postId: string) => {
    const url = `${window.location.origin}/prestador/${postId}`;
    
    switch (type) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(`Confira este profissional: ${url}`)}`);
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`);
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`);
        break;
      case 'copy':
        await navigator.clipboard.writeText(url);
        toast.success('Link copiado para a área de transferência');
        break;
    }
    
    setShowShare(false);
  };

  if (loading) {
    return (
      <div>
        <Navbar onLogout={handleLogout} onCityChange={handleCityChange} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar onLogout={handleLogout} onCityChange={handleCityChange} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-12 gap-6">
          {/* Left Column - Profile (hidden on mobile) */}
          <div className="hidden lg:block lg:col-span-3">
            {profile && <ProfileCard profile={profile} rating={averageRating} />}
          </div>

          {/* Middle Column - Posts */}
          <div className="col-span-12 lg:col-span-6 space-y-6">
            {/* Create Post */}
            <Card className="p-6">
              <div className="flex items-center space-x-4">
                <Avatar className="h-10 w-10">
                  <div className="bg-blue-600 w-full h-full flex items-center justify-center text-white">
                    {profile?.name?.charAt(0).toUpperCase()}
                  </div>
                </Avatar>
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-gray-500"
                  onClick={() => setShowCreatePost(true)}
                >
                  Compartilhe seus serviços...
                </Button>
              </div>
            </Card>

            {/* Posts Feed */}
            {posts.length === 0 ? (
              <Card className="p-6 text-center">
                <p className="text-gray-500">Nenhuma publicação encontrada para esta cidade</p>
              </Card>
            ) : (
              posts.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  currentUserId={auth.currentUser?.uid || ''}
                  onNavigateToProfile={navigateToProfile}
                  onShowShare={(post) => {
                    setSelectedPost(post);
                    setShowShare(true);
                  }}
                  onDeletePost={handleDeletePost}
                  onReportPost={handleReportPost}
                  onSavePost={handleSavePost}
                />
              ))
            )}
          </div>

          {/* Right Column - Ratings (hidden on mobile) */}
          <div className="hidden lg:block lg:col-span-3">
            <RatingsCard
              rating={averageRating}
              totalReviews={ratings.length}
              ratingBreakdown={[
                { stars: 5, percentage: ratings.filter(r => r.rating === 5).length / ratings.length * 100 || 0 },
                { stars: 4, percentage: ratings.filter(r => r.rating === 4).length / ratings.length * 100 || 0 },
                { stars: 3, percentage: ratings.filter(r => r.rating === 3).length / ratings.length * 100 || 0 },
                { stars: 2, percentage: ratings.filter(r => r.rating === 2).length / ratings.length * 100 || 0 },
                { stars: 1, percentage: ratings.filter(r => r.rating === 1).length / ratings.length * 100 || 0 }
              ]}
              recentReview={ratings[0] ? {
                rating: ratings[0].rating,
                comment: ratings[0].comment,
                author: ratings[0].authorName,
                date: "Recente"
              } : {
                rating: 0,
                comment: "Nenhuma avaliação ainda",
                author: "",
                date: ""
              }}
            />
          </div>
        </div>
      </div>

      <CreatePostDialog
        open={showCreatePost}
        onOpenChange={setShowCreatePost}
        onCreatePost={handleCreatePost}
      />

      <CommentsDialog
        open={showComments}
        onOpenChange={setShowComments}
        comments={comments}
        newComment={newComment}
        onNewCommentChange={(value) => setNewComment(value)}
        onAddComment={handleAddComment}
      />

      <ShareDialog
        open={showShare}
        onOpenChange={setShowShare}
        selectedPost={selectedPost}
        onShare={handleShare}
      />
    </div>
  );
}